<template>
    <div id="offers"
        :class="showOfferCards? 'px-4 widescreen:px-0 pt-5 tablet:pt-16 pb-5 bg-white desktop:pt-28': ''">
        <div
            v-if="showOfferCards"
            class="relative container desktop:rounded-md p-0 tablet:pb-4 desktop:pb-8 desktop:px-4 desktop:border-4 desktop:border-blue-light"
            :class="[{'desktop:pt-16': !showNoTvAvailableContent}]"
        >
            <div class="text-center desktop:px-6 desktop:absolute desktop:-top-6 pb-4 desktop:left-1/2 desktop:transform desktop:-translate-x-1/2 desktop:-translate-y-1/2 bg-white">
                    <h1 v-if="headingTag" class="desktop:whitespace-nowrap text-lg desktop:text-3xl" v-html="heading"></h1>
                    <h1 v-else class="desktop:whitespace-nowrap text-lg desktop:text-3xl" v-html="heading"></h1>
            </div>

            <div
                v-if="showNoTvAvailableContent && isServiceable"
                class="flex flex-col gap-y-1 items-center mt-8 px-6 py-5 border border-solid border-border rounded-md bg-gray-100 text-center text-navy-dark"
            >
                <IconSquareExclamation class="text-3xl" />
                <div class="text-md desktop:text-2lg font-bold">TV service isn’t currently available in your area.</div>
                <div class="text-sm desktop:text-lg">But you can find your perfect Internet package below.</div>
            </div>

            <SlottedModal
                :is-immediately-visible="showNoTvAvailableContent && this.showTvNotAvailableModal"
                @dismiss="closeTvNotAvailableModal"
            >
                <template #contents>
                    <div class="text-center">
                        <div class="text-2xl text-navy-dark mb-9">
                          TV service isn’t currently available in your area.
                        </div>
                        <a
                            href="/internet"
                            @click.prevent="onShopInternetServiceClick"
                            class="btn-primary"
                        >
                            Shop Internet Packages
                        </a>

                    </div>
                </template>
            </SlottedModal>

            <div
                v-if="!isServiceable"
                class="text-center"
            >
                <h3>{{ nonServiceableTitle }}</h3>
                <p>{{ nonServiceableMessage }}</p>
            </div>
            <div v-if="meta.customerState !== 'current'"
                class="grid grid-cols-1 gap-4 items-start justify-items-center mb-4 tablet:mb-9 desktop:mb-8 mx-auto"
                :class="[
                    {'tablet:grid-cols-2': visibleOffers.length > 1},
                    'desktop:grid-cols-' + visibleOffers.length,
                    {'max-w-3xl': visibleOffers.length <= 2},
                    {'max-w-6xl': visibleOffers.length === 3},
                    {'desktop:mt-10': showNoTvAvailableContent},
                    {'pt-8': showNoTvAvailableContent},
                ]"
            >

                <OfferCard
                    v-for="offer in visibleOffers"
                    :key="offer.title"
                    class="flex-1"
                    data-cy="offer-card"
                    v-bind="offer"
                    :gwp-amount="gwpAmount"
                    :promo-link="promoLink"
                    :promo-label="promoLabel"
                    @select="selectOfferProxy(offer)"
                />
            </div>
            <div
                v-else
                class="text-center mb-8"
            >
                <h3>
                    Our records show that you are already a customer
                </h3>
                <p>
                    Please call 844-511-7889 to upgrade your services. Or visit the <a href="/my-services">My Services page</a> to check your account
                </p>
            </div>

            <!-- Toggle for showing/hiding the Broadband Labels. Must be set to true by default -->
            <div v-if="meta.customerState !== 'current'" class="text-center mb-8">
                <span class="mr-2 font-light">Show/Hide Broadband Labels</span>
                    <label class="switch">
                        <input v-model="showBroadbandLabels" type="checkbox" aria-label="Show or Hide Broadband Labels">
                        <span class="slider round"></span>
                    </label>
            </div>

            <!--
                Mobile Devices only, radio buttons to switch between the label for each available speed
                there are some styles for .speed-select styles in the main.scss file that tailwind v2 couldn't make
            -->
            <fieldset v-if="showBroadbandLabels && meta.customerState !== 'current'" class="block desktop:hidden border-0 p-0 m-0 text-center">
                <div class="font-bold rounded-full inline-block bg-blue-polar-light">
                    <span v-for="(offer, index) in visibleOffers" :key="index" class="speed-select inline-block relative">
                        <input
                            class="appearance-none opacity-min bg-none absolute top-0 w-full z-10 border-2 border-solid h-full left-0"
                            type="radio"
                            v-model="selectedProductIndex"
                            :value="index"
                            :id="'r' + index"
                            name="radios"
                            :checked="index === selectedProductIndex">
                        <label
                            class="block py-0.5 px-5 rounded-full border-2 border-solid border-transparent text-navy-dark"
                            :for="'r' + index">
                            {{filterDownloadSpeed(offer.download)}}<br>Mbps
                        </label>
                    </span>
                </div>
            </fieldset>

            <!-- Labels error state for non-buyflow -->
            <div 
                v-if="showBroadbandLabels && meta.customerState !== 'current' && !labelAvailable && !labelsAreLoading" 
                class="max-w-4xl mx-auto mb-8 p-6 text-center bg-red-50 border border-red-200 rounded-lg">
                <h3 class="text-red-700 font-semibold mb-2"> We are experiencing technical difficulties.</h3>
                <p class="text-red-600">
                    Please visit your MyAccount online portal later to view a label describing key features of your Breezeline's service option.
                </p>
            </div>

            <!-- The Broadband Labels in English-->
            <div
                v-else-if="meta.customerState !== 'current' && showBroadbandLabels"
                class="label-wrapper grid grid-cols-1 tablet:gap-4 items-start justify-items-center mb-4 tablet:mb-9 desktop:mb-8 tablet:mt-8 desktop:mt-auto mx-auto"
                :class="[
                    'desktop:grid-cols-' + visibleOffers.length,
                    {'max-w-3xl': visibleOffers.length <= 2},
                    {'max-w-6xl': visibleOffers.length === 3},
                    `selected-${selectedProductIndex}`
                ]"
            >
            <BroadbandLabel
                v-for="(offer, index) in visibleOffers" 
                :key="index"
                :offerTitle="offer.title"
                :showBroadbandLabels="showBroadbandLabels"
                :selectedLabel="visibleOffers[selectedProductIndex].title"
                v-bind="offer"
                :all-data="offer.allData"
                :hsdPromoPrice="offer.hsdPromoPrice"
                :hsdFullPrice="offer.hsdFullPrice"
                :isBuyflow=false
                :class="[
                    'broadband-label-item',
                    `label-index-${index}`
                ]"
            />
            </div>

            

            <div v-if="letsGetStarted && isServiceable && displayGetStartedCta"
                class="text-center">
                <h3 class="text-lg px-8 pt-4">{{ planDetailsHeading }}</h3>
                <a
                    href="/lets-get-started"
                    class="w-full desktop:w-auto btn-secondary track mt-4"
                    data-category="resi: calculator button"
                    :data-action="`resi: calculator ${ ctaText.toLowerCase() }`"
                    :data-label="`resi: calculator ${ currentPage } product page layer 1 button`">
                        {{ letsGetStarted }}
                </a>
            </div>
            <div v-if="isServiceable"
                 class="text-center text-sm pt-4">
                <p>
                    *Includes up to a $10 AutoPay & EcoSave Discount. Displayed offers are based on your estimated ZIP Code. Services, pricing and availability are subject to change based on your specific service location.<br>

                    <span
                        v-if="promoLink"
                        class="block desktop:inline mt-4 desktop:mt-0">
                        <a
                            :href="promoLink"
                            class="text-navy underline"
                            target="_blank">
                            {{ promoLabel }}
                        </a>
                        -
                    </span>
                    <a href="/policies-agreements"
                       class="text-navy underline">
                        Terms and Conditions
                    </a>

                </p>
            </div>
            <div v-if="getCurrentAddressAsString(this)"
                 class="text-center flex flex-col pt-4 text-sm">
                <strong class="text-navy-dark">
                    Current Location
                </strong>
                <p>
                    {{ getCurrentAddressAsString(this) }}
                </p>
                <a @click="changeLocation()"
                   class="track underline font-bold"
                   data-category="Verify Address not into buyflow">
                    Change?
                </a>
            </div>
            <div v-else
                 class="text-center
                        pt-4">
                <a @click="changeLocation()"
                   class="text-sm underline font-bold">
                    Check Availability?
                </a>
            </div>
        </div>
        <SlottedModal :is-immediately-visible="maintenancePopup"
                      modal-type="warning"
                      @dismiss="maintenancePopup = false">
            <template #contents>
                <div class="text-base font-bold mb-2">
                    <IconTriangleExclamation class="inline mr-1" /> Online ordering is currently under maintenance
                </div>
                <div>
                    Online ordering as well as account management features<br>on breezeline.com and in our My Breezeline mobile app<br>are currently unavailable due to scheduled maintenance.<br>We apologize for any inconvenience.
                </div>
            </template>
        </SlottedModal>
        <SlottedModal v-if="mobilePopup"
                      :is-immediately-visible="mobilePopup"
                      modal-type="transparent"
                      @dismiss="mobilePopup = false">
            <template #contents>
                <div class="text-center pt-5 pb-2 px-2 mx-5">
                    <h3>
                        Breezeline Mobile is coming to your area soon!
                    </h3>
                    <p class="pt-2 pb-2">
                        Breezeline Mobile service is not yet available at your address. Save time by signing up for internet service now, and we’ll contact you when mobile is available.
                    </p>
                    <button
                        @click="sendEvent('resi: mobile landing page',null,null,'mobile_product_page_ineligible_proceed_checkout'); continueSelectOffer(selectedOffer)"
                        class="btn-primary mt-4">
                        Proceed to Checkout
                    </button>
                    <p class="pt-8 pb-2">
                        If you’d like to wait to sign up for Breezeline Internet & Mobile together, please complete this form and we will notify you when mobile is available.
                    </p>
                    <a
                        href="/mobile-is-coming"
                        @click="sendEvent('resi: mobile landing page',null,null,'mobile_product_page_ineligible_notify_me_lead')"
                        class="btn-primary mt-4">
                        Notify me When Mobile is Available
                    </a>
                </div>
            </template>
        </SlottedModal>
    </div>
</template>

<script>
import OfferCard from '@/molecules/OfferCard'
import { mapActions, mapState, mapGetters } from 'vuex'
import SlottedModal from '../SlottedModal'
import { DateTime as dt } from 'luxon'
import IconSquareExclamation from "@/atoms/icons/IconSquareExclamation"
import products from '../../../templates/products/_products'
const _ = require('lodash')
import BroadbandLabel from '@/organisms/BroadbandLabel'

export default {
    name: 'OfferCards',
    components: {
        SlottedModal,
        OfferCard,
        IconSquareExclamation,
        BroadbandLabel
    },
    props: {
        productMetadata: {
            type: String,
            required: false,
            default: "[]",
        },
        offerType: {
            type: String,
            required: true,
            default: 'internet',
            validator: val => [
                'internet',
                'internet-equipment',
                'internet-tv',
                'internet-voice',
                'custom',
            ].includes(val),
        },
        customType: {
            type: String,
            required: false,
            default: "[]",
        },
        heading: {
            type: String,
            required: true,
            default: null,
        },
        subheading: {
            type: String,
            required: true,
            default: null,
        },
        headingTag: {
            type: String,
            default: null,
        },
        ctaText: {
            type: String,
            default: 'Find Your Perfect Speed',
        },
        planDetailsHeading: {
            type: String,
            default: 'Not sure which plan is right for you?',
        },
        promoLabel: {
            type: String,
            default: null,
        },
        promoLink: {
            type: String,
            default: null,
        },
        reRoute: {
            type: String,
            required: false,
        },
        displayBackground: {
            type: Boolean,
            required: false,
            default: true,
        },
        displayGetStartedCta: {
            type: Boolean,
            required: false,
            default: true,
        },
        banner: {
            type: [Object, null],
            default: null,
        },
    },
    data: () => ({
        // NOTE hook on to this to show/hide a loading overlay within the component
        // NOTE true on 2 things: 1) offers are loading 2) an offer was selected and we're getting rerouted to Buyflow
        isLoading: false,
        cookiePromotion: null,
        maintenancePopup: false,
		    mobilePopup: false,
        selectedOffer: null,
        postalCode: '',
        showBroadbandLabels: true,
        selectedProductIndex: 0,
    }),
    computed: {
        ...mapState([
            'meta',
            'env',
            'residentialOffers',
            'showTvNotAvailableModal',
        ]),
        ...mapState('BroadbandLabels', [
            'labelsAreLoading',
            'labelAvailable',
            'allLabels'
        ]),
        ...mapGetters([
            'getCurrentAddressAsString',
            'showOfferCards',
            'isPendingDisconnect',
            'offersByType',
			      'isGwp',
			      'gwpAmount',
            'offers',
            'parseMetadata',
            'filterAndMerge',
        ]),
        ...mapGetters('Buyflow',[
            'isOhio',
        ]),

        visibleOffers() {
            const offers = this.offersByType({mergedMetadata: this.mergedMetadata, offerType: this.offerType, customType: JSON.parse(this.customType)});
            // Set CSS variable for number of columns
            document.documentElement.style.setProperty('--columns', offers.length);
            return offers;
        },
        productData () {
            return {
                file: this.parseMetadata(products ?? []),
                craft: this.parseMetadata(JSON.parse(this.productMetadata) ?? [])
            }
        },
        isServiceable(){
            return this.visibleOffers.length
        },

        showNoTvAvailableContent() {
            let showContent = false

            if (this.offerType === 'internet-tv') {
                Object.keys(this.visibleOffers).forEach(key => {
                  const offerAllData = this.visibleOffers[key].allData
                  const hasTvInOffer = offerAllData.hasOwnProperty('tv')
                  if (!hasTvInOffer) {
                      showContent = true
                  }
              })
            }
            return showContent
        },

        mergedMetadata() {
            return this.filterAndMerge({base: this.productData.file, override: this.productData.craft})
        },

        skeletonTitle() {
            return ({
                'internet': 'UltraFast',
                'internet-equipment': 'UltraFast + WiFi Your Way&trade; Home',
                'internet-tv': 'UltraFast + TV',
                'internet-voice': 'UltraFast + Voice',
            })[this.offerType]
        },
        letsGetStarted() {
            return window.location.pathname === '/florida'? 'Compara velocidades': this.ctaText
        },
        nonServiceableTitle(){
            return ({
                'internet': 'Internet Services',
                'internet-equipment': 'WiFi Your Way&trade; Home',
                'internet-tv': 'TV Services',
                'internet-voice': 'Unlimited Voice Service',
            })[this.offerType]
        },
        nonServiceableMessage(){
            return ({
                'internet': 'Internet Services are not currently available in your area at this time.',
                'internet-equipment': 'WiFi Your Way&trade; Home is not currently available in your area at this time.',
                'internet-tv': 'TV Services are not currently available in your area at this time.',
                'internet-voice': 'Unlimited Phone Service is not currently available in your area at this time.',
            })[this.offerType]
        },
        currentPage() {
            let pageTitle = window.location.pathname.replace(/-/g, ' ').replace(/\\|\//g,' ').substring(1);
            if(pageTitle == ""){
                pageTitle = "home";
            }
            return pageTitle;
        },
        onMobileLP() {
            return window.location.pathname === '/internet-mobile'
        },
    },
    watch: {
        visibleOffers (value) {
            if (value.length){
                this.ecommerceOfferView()
            }
        }
    },
    mounted () {
        if (this.visibleOffers.length > 0){
            this.ecommerceOfferView()
        }

        const queryString = window.location.search;

        if(queryString && new URLSearchParams(queryString).get('i')){
            const params    = new URLSearchParams(queryString)
            const internet  = params.getAll('i') || []
            const tv        = params.getAll('t') || []
            const voice     = params.getAll('v') || []
            const equipment = params.getAll('e') || []
            let selected    = []

            window.history.pushState({}, document.title, window.location.pathname)

            if (tv.length){
                selected = [{
                    name: tv[0],
                    type: "tv",
                    decisionGroup: tv[0].indexOf('Stream') > -1? "VE1 Stream TV": "VE1 TiVo Experience"
                }]
            } else if (voice.length){
                selected = [{
                    name: voice[0],
                    type: "voice",
                    decisionGroup: "PS1 Phone Service",
                }]
            } else if (equipment.length){
                selected = [{
                    name: equipment[0],
                    type: "internet-equipment",
                    decisionGroup: equipment[0].indexOf('WiFi') > -1? "HE3 - WYW": "HE1 Modem & Plume"
                }]
            }

            if (internet.length && internet[0] === 'open') {
                this.toggleCheckAvailabilityModal({
                    visible: true,
                    routeToBuyflow: false,
                    resetSelectedOffer: true,
                    currentCustomerCheck: false,
                })

            } else {
                this.selectOfferProxy({
                    name: internet[0],
                    selected,
                    sku: this.visibleOffers.find(offer => offer.name === internet[0])?.sku ?? null,
                    promoPrice: this.visibleOffers.find(offer => offer.name === internet[0])?.promoPrice ?? null,
                    backupNames: internet.splice(1),
                })
            }
        }

        if (this.showNoTvAvailableContent && this.showTvNotAvailableModal) {
            this.gaPushEvent('popup', 'impression', 'TV service not available')
        }

    },
    methods: {
        ...mapActions([
            'toggleCheckAvailabilityModal',
            'fetchAvailableOffers',
            'selectOffer',
            'setSelectedOffer',
            'setEcommerceItem'
        ]),
        ...mapActions('Personalization',[
            'ping',
        ]),
        ...mapActions('Promotions',[
            'applyPromotion',
        ]),
        ...mapActions('BroadbandLabels', [
            'updateLabelRequest',
            'setLabelStatus'
        ]),
        ...mapActions({
          setShowNoTvAvailableModal: 'setShowNoTvAvailableModal',
        }),
        ecommerceOfferView() {
            gtag("event", "view_item_list", {
                items: this.visibleOffers.map(function(offer, index){
                    return {
                        name: offer.name,
                        list_name: offer.type + ' list',
                        brand: "Breezeline Online",
                        category: offer.type,
                        variant: offer.title,
                        list_position: index + 1,
                        price: offer.promoPrice,
                        dimension1: offer.download,
                        metric1: offer.fullPrice
                    }
                }),
            });
        },
        changeLocation() {
            if (this.env.buyflowMaintenance == true){
                if (this.env.buyflowMaintenanceTimeframe) {
                    const now = dt.now()
                    const timeframe = this.env.buyflowMaintenanceTimeframe.split(',').map(function (time) {
                        return dt.fromFormat(time, 'yyyy-MM-dd\'T\'HH:mm:ss')
                    })

                    if ((timeframe.length > 1 && timeframe[0].isBefore(now) && timeframe[1].isAfter(now)) || (timeframe.length === 1 && timeframe[0].isBefore(now))) {
                        this.maintenancePopup = true
                    } else {
                        this.toggleCheckAvailabilityModal({
                            visible: true,
                            routeToBuyflow: false,
                            resetSelectedOffer: true,
                            currentCustomerCheck: false,
                        })
                    }
                } else {
                    this.maintenancePopup = true
                }
            } else {
                this.toggleCheckAvailabilityModal({
                    visible: true,
                    routeToBuyflow: false,
                    resetSelectedOffer: true,
                    currentCustomerCheck: false,
                })
            }
        },
        selectOfferProxy(offer) {
            //Apply promotions
            // Add available promotions here from promo object
            const applyPromotion = this.$store.dispatch('Promotions/applyPromotion', { offer });

             // Update labelRequest with only the selected plan's request
             this.updateLabelRequest(offer.name);

            if (this.env.buyflowMaintenance == true){
                if (this.env.buyflowMaintenanceTimeframe) {
                    let now = dt.now()
                    const timeframe = this.env.buyflowMaintenanceTimeframe.split(',').map(function (time) {
                        return dt.fromFormat(time, 'yyyy-MM-dd\'T\'HH:mm:ss')
                })

                    if ((timeframe.length > 1 && timeframe[0].isBefore(now) && timeframe[1].isAfter(now)) || (timeframe.length === 1 && timeframe[0].isBefore(now))) {
                        this.maintenancePopup = true
                    } else {
						this.$store.commit('RESET_MOBILE')
						if (this.onMobileLP && !this.meta.mobileEligible) {
							this.selectedOffer = offer
							this.mobilePopup = true
						} else {
							this.continueSelectOffer(offer)
						}
                    }
                } else {
                    this.maintenancePopup = true
                }
            } else {
				this.$store.commit('RESET_MOBILE')
                if (this.onMobileLP && !this.meta.mobileEligible) {
					this.selectedOffer = offer
                    this.mobilePopup = true
                } else {
                    this.continueSelectOffer(offer)
                }
            }
        },
        continueSelectOffer(offer){
			      this.mobilePopup = false;
			      if (this.onMobileLP) {
				        this.$store.commit('FROM_MOBILE_LP', true)
            }
            this.setSelectedOffer({selectedOffer: offer})

            const items = this.visibleOffers.map(function (offer) {
                return {
                    name: offer.name,
                    variant: offer.title,
                    list_name: offer.type + ' list',
                    brand: "Breezeline Online",
                    category: offer.type,
                    price: offer.promoPrice,
                    metric1: offer.fullPrice,
                    dimension1: offer.download,
                }
            })
            this.setEcommerceItem({item: items[0],})

            gtag("event", "view_item", {items,})
            gtag("event", "add_to_cart", {items,})

            if (offer?.sku !== null) {
                this.ping({
                    eventName: 'ADD_TO_CART',
                    payload: {
                        source: {
                            pageType: 'cart',
                            url: window.location.href,
                            urlReferrer: document.referrer,
                            locale: 'en_US',
                        },
                        cart: {
                            complete: {
                                Product: [
									{
										_id: offer.sku,
										price: offer.promoPrice?.toString(),
										quantity: '1',
									},
									...offer.variants.map(function (variant) {
										return {
											_id: variant.sku,
											price: variant.promoPrice?.toString(),
											quantity: '1',
										}
									}),
								],
                            },
                        },
                    }
                })
            }

            if (this.meta.locationId && this.meta.billerMappingCode && (this.meta.isCurrentCustomer !== true || this.isPendingDisconnect)) {
                if(this.isOhio && this.isPendingDisconnect) {
                    // removes the SIK ship only error from occurring
                    this.setSelectedOffer({
                        selectedOffer: {
                            name: this.selectedOffer.name,
                            selected: [
                                ...this.selectedOffer.selected,
                                {
                                    "name":"Professional Install",
                                    "type": "install"
                                },
                            ]
                        },
                    })
                }
                this.selectOffer()
            } else {
                this.toggleCheckAvailabilityModal({
                    visible: true,
                    routeToBuyflow: true,
                    resetSelectedOffer: false,
                    currentCustomerCheck: this.meta.isCurrentCustomer || false,
                })
            }
        },
        filterDownloadSpeed(speed){
            if (speed.includes(',')) {
                return speed.split(',')[1];
            } else {
                return speed;
            }
        },
        closeTvNotAvailableModal() {
            if(this.showTvNotAvailableModal) {
              this.gaPushEvent('popup', 'close', 'TV service not available')
            }
            this.setShowNoTvAvailableModal(false)
        },
        onShopInternetServiceClick() {
            this.gaPushEvent('popup', 'cta_click', 'Shop Internet Packages')
            this.setShowNoTvAvailableModal(false)
            window.location.href = '/internet'
        },
        gaPushEvent(name, action, label) {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                'event': 'event_tracking',
                'event_name': name,
                'event_action': action,
                'event_label': label
            })
        },
    }
}
</script>

<style scoped>
fieldset.block {
    margin-bottom: 5px;
}

.label-wrapper {
    display: grid;
    transition: all 0.3s ease;
}

.broadband-label-item {
    order: 1;
    transition: order 0.3s ease;
}

.label-wrapper.selected-0 .label-index-0,
.label-wrapper.selected-1 .label-index-1,
.label-wrapper.selected-2 .label-index-2,
.label-wrapper.selected-3 .label-index-3 {
    order: 0;
}

/* Add styles for the error message */
.text-red-600 {
    color: rgb(220, 38, 38);
}

.text-red-700 {
    color: rgb(185, 28, 28);
}

.bg-red-50 {
    background-color: rgb(254, 242, 242);
}

.border-red-200 {
    border-color: rgb(254, 202, 202);
}

/* Mobile styles (1023px and below) */
@media (max-width: 1023px) {
    .label-wrapper .broadband-label-item {
        display: none;
    }

    /* Show the first item when none is selected */
    .label-wrapper.selected--1 .label-index-0,
    .label-wrapper.selected-0 .label-index-0,
    .label-wrapper.selected-1 .label-index-1,
    .label-wrapper.selected-2 .label-index-2,
    .label-wrapper.selected-3 .label-index-3 {
        display: block;
    }
}

/* Desktop styles (1024px and above) */
@media (min-width: 1024px) {
    .label-wrapper {
        grid-template-columns: repeat(var(--columns, 4), 1fr);
    }
}
</style>
